import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import Icon from './Icon';
import LegalModal from './LegalModal';
import Spinner from './Spinner';
import { auth } from '../services/firebase';
import { UserRole } from '../types';

declare global {
    interface Window {
        grecaptcha: any;
    }
}

type AuthMode = 'login' | 'register' | 'forgot_password';

const Login: React.FC = () => {
    const { 
        loginWithEmail, 
        registerWithEmail, 
        authError, 
        setAuthError, 
        resendVerification, 
        resetPassword, 
        loginWithGoogle 
    } = useAuth();
    
    const { t } = useLanguage();
    const { theme } = useTheme();
    
    const isMounted = useRef(true);
    
    // UI State
    const [mode, setMode] = useState<AuthMode>('login');
    const [isLegalOpen, setIsLegalOpen] = useState(false);
    const [isFirebaseReady, setIsFirebaseReady] = useState(false);
    
    // Auth Data State
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState(''); 
    const [role, setRole] = useState<UserRole>('shopper');
    const [showPassword, setShowPassword] = useState(false);
    
    // Status State
    const [isLoading, setIsLoading] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    
    // Validation State
    const [emailTouched, setEmailTouched] = useState(false);
    const [passwordTouched, setPasswordTouched] = useState(false);

    // Config Check (Robust check from live version)
    const recaptchaKey = process.env.VITE_RECAPTCHA_SITE_KEY || process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    // --- INITIALIZATION ---

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        if (auth && isMounted.current) setIsFirebaseReady(true);
    }, []);

    // ReCAPTCHA V3 Script Loader (Only for Register)
    useEffect(() => {
        if (mode === 'register' && recaptchaKey) {
            if (!document.getElementById('recaptcha-v3-script')) {
                const script = document.createElement('script');
                script.id = 'recaptcha-v3-script';
                script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaKey}`;
                script.async = true;
                document.body.appendChild(script);
            }
        }
    }, [mode, recaptchaKey]);

    // --- HELPERS ---

    // Updated Password Logic (from New version)
    const isPasswordPolicyMet = useMemo(() => {
        return password.length >= 6 && password.length <= 10;
    }, [password]);

    const passwordStrength = useMemo(() => {
        if (!password) return 0;
        let score = 0;
        if (password.length >= 6) score += 1;
        if (password.length >= 8) score += 1;
        if (/[A-Z]/.test(password)) score += 1;
        if (/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password)) score += 1;
        return Math.min(score, 4);
    }, [password]);

    const isEmailValid = useMemo(() => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }, [email]);

    const getFriendlyErrorMessage = (errorCode: string) => {
        switch (errorCode) {
            case 'auth/invalid-email': return "The email address is invalid.";
            case 'auth/user-disabled': return "This account has been disabled.";
            case 'auth/user-not-found': return "No account found with this email.";
            case 'auth/wrong-password': return "Incorrect password.";
            case 'auth/invalid-credential':
            case 'auth/invalid-login-credentials': return "Incorrect email or password.";
            case 'auth/email-already-in-use': return "Email already in use. Please sign in.";
            case 'auth/weak-password': return "Password is too weak.";
            case 'auth/too-many-requests': return "Too many attempts. Try again later.";
            case 'auth/network-request-failed': return "Network error. Check connection.";
            default: return "Authentication failed. Please try again.";
        }
    };

    const executeRecaptchaV3 = async (): Promise<boolean> => {
        if (!recaptchaKey) return true; 

        if (typeof window.grecaptcha === 'undefined') {
            console.warn("ReCAPTCHA V3 not loaded yet.");
            return false; 
        }

        try {
            const token = await window.grecaptcha.execute(recaptchaKey, { action: 'signup' });
            return !!token;
        } catch (e) {
            console.error("ReCAPTCHA V3 Execution Error:", e);
            return false;
        }
    };

    // --- HANDLERS ---

    const handleCredentialSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setAuthError(null);

        if (!isFirebaseReady) { setAuthError("Auth service unavailable."); return; }

        if (mode === 'login') {
            setIsLoading(true);
            try {
                await loginWithEmail(email, password);
            } catch (e: any) {
                if (isMounted.current) {
                    setAuthError(getFriendlyErrorMessage(e.code));
                    setIsLoading(false);
                }
            }
        } 
        else if (mode === 'register') {
            if (!name) { setAuthError("Display name is required"); return; }
            if (password !== confirmPassword) { setAuthError("Passwords do not match"); return; }
            if (!isPasswordPolicyMet) { setAuthError("Password must be between 6 and 10 characters."); return; }
            
            setIsLoading(true);
            const isHuman = await executeRecaptchaV3();
            
            if (!isHuman) {
                setIsLoading(false);
                setAuthError("Security check failed. Please refresh and try again.");
                return;
            }
            
            try {
                await registerWithEmail(name, email, password, role);
                if (isMounted.current) {
                    setVerificationSent(true);
                    setIsLoading(false);
                }
            } catch (e: any) {
                if (isMounted.current) {
                    setAuthError(getFriendlyErrorMessage(e.code));
                    setIsLoading(false);
                }
            }
        } 
        else if (mode === 'forgot_password') {
            if (!isEmailValid) {
                setAuthError("Please enter a valid email.");
                return;
            }
            setIsLoading(true);
            try {
                await resetPassword(email);
                setResetEmailSent(true);
            } catch (e: any) {
                setAuthError(getFriendlyErrorMessage(e.code));
            }
            setIsLoading(false);
        }
    };

    // Unified Google Logic (Custom Button Logic from Live)
    const handleGoogleClick = async () => {
        setAuthError(null);
        setIsLoading(true);
        
        try {
            // Default role is 'shopper' if creating new account via Google
            await loginWithGoogle('shopper'); 
        } catch (e: any) {
            if (isMounted.current) {
                setIsLoading(false);
                setAuthError(getFriendlyErrorMessage(e.code));
            }
        }
    };

    const resetView = () => {
        setVerificationSent(false);
        setResetEmailSent(false);
        setMode('login');
        setAuthError(null);
        setIsLoading(false);
        setEmail('');
        setPassword('');
    };

    // --- RENDER ---

    return (
        <div className="flex flex-col lg:flex-row min-h-[100dvh] w-full bg-[#f8f9fa] dark:bg-black transition-colors duration-500 font-sans">
            
            {/* Left Hero (New UI) */}
            <div className="lg:w-1/2 w-full lg:h-auto h-[35vh] relative bg-black shrink-0 overflow-hidden">
                <div className="absolute inset-0 bg-black/40 z-10"></div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent z-20 opacity-80 lg:opacity-60"></div>
                <img 
                    src="https://images.unsplash.com/photo-1601121141461-9d6647bca1ed?q=80&w=2500&auto=format&fit=crop" 
                    alt="Heritage Jewelry" 
                    className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-16 z-30 flex flex-col items-start">
                    <div className="w-16 h-16 mb-6 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-1000">
                        <Icon name="diamond" className="w-8 h-8 text-apple-gold" />
                    </div>
                    <h1 className="text-3xl lg:text-5xl font-display font-bold text-white mb-3 tracking-tight drop-shadow-lg animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-100">
                        Najeweller AI
                    </h1>
                    <p className="text-white/80 text-sm lg:text-lg font-light leading-relaxed max-w-md animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-200">
                        Where Indian Heritage meets European Precision. <br className="hidden lg:block"/>
                        Design bespoke jewelry with the power of AI.
                    </p>
                </div>
            </div>

            {/* Right Form Area (New UI) */}
            <div className="lg:w-1/2 w-full flex flex-col items-center justify-center p-6 lg:p-12 -mt-10 lg:mt-0 relative z-40">
                <div className="w-full max-w-[440px] bg-white dark:bg-[#1c1c1e] rounded-[2rem] shadow-2xl shadow-slate-200/50 dark:shadow-black/50 p-8 lg:p-12 border border-slate-100 dark:border-white/5 animate-in slide-in-from-bottom-8 duration-700 transition-all">
                    
                    {/* Success States */}
                    {verificationSent || resetEmailSent ? (
                        <div className="text-center space-y-6 py-4">
                            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto ${verificationSent ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>
                                <Icon name="check" className="w-10 h-10" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                                    {verificationSent ? "Verify Email" : "Check Inbox"}
                                </h2>
                                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed">
                                    {verificationSent 
                                        ? "We've sent a verification link to your email." 
                                        : "Password reset instructions have been sent to your email."}
                                    <br/>
                                    <span className="font-bold text-slate-900 dark:text-white">{email}</span>
                                </p>
                            </div>
                            <button onClick={resetView} className="w-full py-3.5 bg-slate-900 dark:bg-white text-white dark:text-black rounded-full font-bold text-sm hover:scale-[1.02] active:scale-95 transition-all shadow-lg">
                                Back to Sign In
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Header */}
                            <div className="text-center mb-8">
                                <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-900 dark:text-white tracking-tight mb-2">
                                    {mode === 'login' && 'Welcome Back'}
                                    {mode === 'register' && 'Create Account'}
                                    {mode === 'forgot_password' && 'Reset Password'}
                                </h2>
                                <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">
                                    {mode === 'login' && 'Sign in to access your jewelry designs'}
                                    {mode === 'register' && 'Start designing in seconds'}
                                    {mode === 'forgot_password' && 'We’ll help you recover your account'}
                                </p>
                            </div>

                            {/* Error Banner */}
                            {(!isFirebaseReady || authError) && (
                                <div className="mb-6 p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-500/20 flex gap-3 items-start animate-in fade-in slide-in-from-top-2">
                                    <Icon name="close" className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="text-sm text-red-600 dark:text-red-400 font-bold leading-tight">
                                            {!isFirebaseReady ? "System Error: Auth not configured." : authError}
                                        </p>
                                        {authError && authError.includes("verify") && (
                                            <button onClick={async () => {
                                                setIsLoading(true);
                                                await resendVerification(email, password);
                                                setIsLoading(false);
                                                setAuthError("Verification email resent.");
                                            }} className="text-xs font-bold underline text-red-700 dark:text-red-300 mt-1 block">
                                                Resend Email
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Custom Google Button */}
                            {mode !== 'forgot_password' && (
                                <div className="mb-8">
                                    <div className="w-full flex justify-center min-h-[48px]">
                                        <button 
                                            onClick={handleGoogleClick}
                                            disabled={!isFirebaseReady || isLoading}
                                            className="relative flex items-center justify-center gap-3 w-full py-3.5 px-4 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl hover:bg-slate-50 dark:hover:bg-white/10 transition-all shadow-sm group disabled:opacity-70 disabled:cursor-not-allowed"
                                        >
                                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.24.81-.6z" />
                                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                            </svg>
                                            <span className="text-sm font-bold text-slate-600 dark:text-slate-200">
                                                {isLoading ? 'Connecting...' : 'Continue with Google'}
                                            </span>
                                        </button>
                                    </div>
                                    
                                    <div className="relative flex py-5 items-center">
                                        <div className="flex-grow border-t border-slate-200 dark:border-white/10"></div>
                                        <span className="flex-shrink-0 mx-4 text-[10px] uppercase font-bold tracking-widest text-slate-400">Or with Email</span>
                                        <div className="flex-grow border-t border-slate-200 dark:border-white/10"></div>
                                    </div>
                                </div>
                            )}

                            {/* Email/Pass Form */}
                            <form onSubmit={handleCredentialSubmit} className="space-y-4">
                                {mode === 'register' && (
                                    <>
                                        <div className="relative group animate-in slide-in-from-top-1">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <Icon name="user" className="h-5 w-5 text-slate-400 group-focus-within:text-apple-gold transition-colors" />
                                            </div>
                                            <input 
                                                type="text" 
                                                value={name} 
                                                onChange={(e) => setName(e.target.value)} 
                                                className="block w-full pl-11 pr-4 py-3.5 bg-slate-50 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-apple-gold/50 transition-all" 
                                                placeholder="Display Name" 
                                                required 
                                            />
                                        </div>
                                        <div className="relative group animate-in slide-in-from-top-1">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <Icon name="briefcase" className="h-5 w-5 text-slate-400 group-focus-within:text-apple-gold transition-colors" />
                                            </div>
                                            <select
                                                value={role}
                                                onChange={(e) => setRole(e.target.value as UserRole)}
                                                className="block w-full pl-12 pr-4 py-3.5 bg-slate-50 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-medium text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-apple-gold/50 transition-all appearance-none cursor-pointer"
                                            >
                                                <option value="shopper">Shopping for myself</option>
                                                <option value="business">Jewelry Business Owner</option>
                                                <option value="designer">Jewelry Designer</option>
                                                <option value="student">Student</option>
                                            </select>
                                            <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-400">
                                                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/></svg>
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center border transition-all ${emailTouched ? (isEmailValid ? 'border-green-500 text-green-500' : 'border-red-400 text-red-400') : 'border-slate-300 text-slate-300 dark:border-white/20'}`}>
                                            <span className="text-[10px] font-bold">@</span>
                                        </div>
                                    </div>
                                    <input 
                                        type="email" 
                                        value={email} 
                                        onChange={(e) => setEmail(e.target.value)} 
                                        onBlur={() => setEmailTouched(true)} 
                                        className={`block w-full pl-11 pr-4 py-3.5 bg-slate-50 dark:bg-black/40 border rounded-xl text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${emailTouched && !isEmailValid ? 'border-red-300 focus:ring-red-200' : 'border-slate-200 dark:border-white/10 focus:ring-apple-gold/50'}`} 
                                        placeholder="Email Address" 
                                        required 
                                    />
                                </div>

                                {mode !== 'forgot_password' && (
                                    <div className="space-y-2">
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <Icon name="lock" className="h-5 w-5 text-slate-400 group-focus-within:text-apple-gold transition-colors" />
                                            </div>
                                            <input 
                                                type={showPassword ? "text" : "password"} 
                                                value={password} 
                                                onChange={(e) => setPassword(e.target.value)} 
                                                onBlur={() => setPasswordTouched(true)} 
                                                className="block w-full pl-11 pr-12 py-3.5 bg-slate-50 dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-xl text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-apple-gold/50 transition-all" 
                                                placeholder="Password" 
                                                required 
                                                minLength={6} 
                                                maxLength={10}
                                            />
                                            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
                                                <Icon name={showPassword ? "eye" : "eye-off"} className="h-5 w-5" />
                                            </button>
                                        </div>
                                        
                                        {/* Updated Password Strength Meter */}
                                        {mode === 'register' && (
                                            <div className="mt-3 px-1 animate-in slide-in-from-top-1">
                                                <div className="flex gap-1 h-1 mb-2">
                                                    {[...Array(4)].map((_, i) => (
                                                        <div 
                                                            key={i} 
                                                            className={`flex-1 rounded-full transition-all duration-300 ${
                                                                i < passwordStrength 
                                                                    ? (passwordStrength < 2 ? 'bg-red-500' : passwordStrength < 3 ? 'bg-amber-400' : 'bg-green-500') 
                                                                    : 'bg-slate-200 dark:bg-white/10'
                                                            }`} 
                                                        />
                                                    ))}
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Password Strength</p>
                                                    <div className={`flex items-center gap-1 text-[10px] font-bold transition-colors duration-300 ${isPasswordPolicyMet ? 'text-green-600 dark:text-green-400' : 'text-slate-400'}`}>
                                                        {isPasswordPolicyMet && <Icon name="check" className="w-3 h-3" />}
                                                        <span>6-10 Chars</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {mode === 'register' && (
                                    <div className="relative group animate-in slide-in-from-top-1">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                            <Icon name="lock" className="h-5 w-5 text-slate-400 group-focus-within:text-apple-gold transition-colors" />
                                        </div>
                                        <input 
                                            type="password" 
                                            value={confirmPassword} 
                                            onChange={(e) => setConfirmPassword(e.target.value)} 
                                            className={`block w-full pl-11 pr-4 py-3.5 bg-slate-50 dark:bg-black/40 border rounded-xl text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${confirmPassword && password !== confirmPassword ? 'border-red-300 focus:ring-red-200' : 'border-slate-200 dark:border-white/10 focus:ring-apple-gold/50'}`} 
                                            placeholder="Confirm Password" 
                                            required 
                                            minLength={6} 
                                            maxLength={10} 
                                        />
                                    </div>
                                )}

                                {mode === 'register' && (
                                    <div className="text-[10px] text-slate-400 text-center px-4 animate-in fade-in">
                                        This site is protected by reCAPTCHA and the Google 
                                        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="text-apple-gold hover:underline mx-1">Privacy Policy</a> and 
                                        <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="text-apple-gold hover:underline mx-1">Terms of Service</a> apply.
                                    </div>
                                )}

                                <div className="pt-4">
                                    <button 
                                        type="submit" 
                                        disabled={isLoading || !isFirebaseReady || (mode === 'register' && !isPasswordPolicyMet)} 
                                        className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-black rounded-xl font-bold text-base hover:scale-[1.02] active:scale-95 transition-all shadow-xl shadow-slate-900/10 dark:shadow-white/10 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {isLoading ? <Spinner /> : (
                                            mode === 'login' ? 'Sign In' : 
                                            mode === 'register' ? 'Create Account' : 
                                            'Send Reset Link'
                                        )}
                                        {mode === 'register' && !isLoading && <Icon name="arrow-right" className="w-4 h-4" />}
                                    </button>
                                </div>
                            </form>

                            <div className="flex flex-col items-center gap-4 relative z-10 pt-8">
                                {mode === 'login' ? (
                                    <>
                                        <div className="flex items-center gap-2 text-sm font-medium">
                                            <span className="text-slate-500 dark:text-slate-400">New to Najeweller?</span>
                                            <button onClick={() => { setMode('register'); setAuthError(null); }} className="text-slate-900 dark:text-white font-bold hover:underline">Create Account</button>
                                        </div>
                                        <button onClick={() => { setMode('forgot_password'); setAuthError(null); }} className="text-xs font-bold text-apple-gold hover:text-yellow-500 uppercase tracking-widest transition-colors">Forgot Password?</button>
                                    </>
                                ) : mode === 'register' ? (
                                    <div className="flex items-center gap-2 text-sm font-medium">
                                        <span className="text-slate-500 dark:text-slate-400">Already have an account?</span>
                                        <button onClick={() => { setMode('login'); setAuthError(null); }} className="text-slate-900 dark:text-white font-bold hover:underline">Sign In</button>
                                    </div>
                                ) : (
                                    <button onClick={() => { setMode('login'); setAuthError(null); }} className="text-sm font-bold text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors flex items-center gap-2"><Icon name="close" className="w-4 h-4 rotate-45" /> Back to Sign In</button>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <div className="mt-8 text-center max-w-xs">
                    <p className="text-[10px] text-slate-400 font-medium leading-relaxed">
                        By continuing, you agree to our 
                        <button onClick={() => setIsLegalOpen(true)} className="text-slate-600 dark:text-slate-300 hover:text-apple-gold ml-1 transition-colors underline decoration-dotted">Terms of Service</button> and 
                        <button onClick={() => setIsLegalOpen(true)} className="text-slate-600 dark:text-slate-300 hover:text-apple-gold ml-1 transition-colors underline decoration-dotted">Privacy Policy</button>.
                    </p>
                </div>
            </div>

            <LegalModal isOpen={isLegalOpen} onClose={() => setIsLegalOpen(false)} />
        </div>
    );
};

export default Login;